/**
 * Helper to parse a date string formatted as DD/MM/YYYY, MM/YYYY, or standard formats.
 */
export const parseCustomDate = (dateString?: string | null): Date | null => {
  if (!dateString || typeof dateString !== "string") return null;

  const trimmed = dateString.trim();
  const parts = trimmed.split("/").map((p) => parseInt(p.trim(), 10));

  if (parts.length === 3) {
    const [day, month, rawYear] = parts;
    const year = rawYear >= 0 && rawYear <= 99 ? 2000 + rawYear : rawYear;
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
      return d;
    }
    // Fallback: MM/DD/YYYY
    const d2 = new Date(year, day - 1, month);
    if (!isNaN(d2.getTime())) return d2;
  }

  if (parts.length === 2) {
    const [first, rawYear] = parts;
    const year = rawYear >= 0 && rawYear <= 99 ? 2000 + rawYear : rawYear;
    if (isNaN(first) || isNaN(year)) return null;
    return new Date(year, first - 1, 1);
  }

  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Format a Date object to "Month Year", e.g. "Jan 2027"
 */
export const formatMonthYear = (date: Date): string => {
  return date.toLocaleString("en-US", { month: "short", year: "numeric" });
};

/**
 * Format a Date object to "DD Month YYYY", e.g. "01 Nov 2026"
 */
export const formatFullDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export interface IntakeItemParsed {
  rawMonth: string;
  monthDate: Date | null;
  formattedMonth: string;
  rawStatus: string;
  isOpen: boolean;
  rawDeadline?: string;
  deadlineDate: Date | null;
  formattedDeadline: string;
  daysRemaining: number | null; // null if no deadlineDate or past
  index: number;
}

export interface CourseIntakeInfo {
  isOpen: boolean;
  badgeLabel: string;
  badgeVariant: "open" | "closing-soon" | "closed";
  intakeText: string; // e.g. "Jan 2027"
  deadlineText: string; // e.g. "Deadline: 01 Nov 2026"
  daysRemaining: number | null; // e.g. 14
  closingSoonText?: string; // e.g. "14 days left"
  selectedIntake?: IntakeItemParsed;
}

export const getCourseIntakeInfo = (
  intakeDetails?: {
    month?: string[];
    status?: string[];
    deadline?: string[];
  } | null,
  intakePriority?: number
): CourseIntakeInfo => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const months = Array.isArray(intakeDetails?.month) ? intakeDetails!.month : [];
  const statuses = Array.isArray(intakeDetails?.status) ? intakeDetails!.status : [];
  const deadlines = Array.isArray(intakeDetails?.deadline) ? intakeDetails!.deadline : [];

  if (!months.length) {
    return {
      isOpen: false,
      badgeLabel: "Closed",
      badgeVariant: "closed",
      intakeText: "Not Announced",
      deadlineText: "",
      daysRemaining: null,
    };
  }

  // Parse each entry
  const parsedItems: IntakeItemParsed[] = months.map((m, idx) => {
    const mDate = parseCustomDate(m);
    const dStr = deadlines[idx];
    const dDate = parseCustomDate(dStr);
    const statusStr = (statuses[idx] || "").toLowerCase();
    const isOpen = statusStr === "open";

    let daysRemaining: number | null = null;
    if (dDate) {
      dDate.setHours(23, 59, 59, 999);
      const diffMs = dDate.getTime() - today.getTime();
      daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }

    return {
      rawMonth: m,
      monthDate: mDate,
      formattedMonth: mDate ? formatMonthYear(mDate) : m,
      rawStatus: statuses[idx] || "",
      isOpen,
      rawDeadline: dStr,
      deadlineDate: dDate,
      formattedDeadline: dDate ? formatFullDate(dDate) : dStr || "",
      daysRemaining,
      index: idx,
    };
  });

  // Filter open intakes where the intake month is either in the future or deadline hasn't passed
  const openCandidates = parsedItems.filter((item) => {
    if (!item.isOpen) return false;
    if (item.daysRemaining !== null && item.daysRemaining < 0) {
      // Deadline has passed
      return false;
    }
    if (item.monthDate && item.monthDate < today) {
      // Intake month is already in the past
      return false;
    }
    return true;
  });

  if (openCandidates.length > 0) {
    // Sort by earliest intake month date (or earliest deadline)
    openCandidates.sort((a, b) => {
      const aTime = a.monthDate ? a.monthDate.getTime() : Infinity;
      const bTime = b.monthDate ? b.monthDate.getTime() : Infinity;
      return aTime - bTime;
    });

    const active = openCandidates[0];
    const days = active.daysRemaining;
    // Show deadline only when 20 days or fewer left
    const isClosingSoon = days !== null && days >= 0 && days <= 20;

    let deadlineLabel = "";
    if (isClosingSoon && active.formattedDeadline) {
      deadlineLabel = `Deadline: ${active.formattedDeadline}`;
    }

    let closingSoonText: string | undefined = undefined;
    if (isClosingSoon && days !== null) {
      if (days === 0) {
        closingSoonText = "Deadline today";
      } else if (days === 1) {
        closingSoonText = "1 day left";
      } else {
        closingSoonText = `${days} days left`;
      }
    }

    return {
      isOpen: true,
      badgeLabel: isClosingSoon ? "Closing soon" : "Intake open",
      badgeVariant: isClosingSoon ? "closing-soon" : "open",
      intakeText: active.formattedMonth,
      deadlineText: deadlineLabel,
      daysRemaining: days,
      closingSoonText,
      selectedIntake: active,
    };
  }

  // If no open candidates, look for upcoming or priority intake
  const priorityIdx = intakePriority ?? 0;
  const fallback = parsedItems[priorityIdx] || parsedItems[0];

  return {
    isOpen: false,
    badgeLabel: "Closed",
    badgeVariant: "closed",
    intakeText: `Upcoming Intake: ${fallback.formattedMonth || "Not Announced"}`,
    deadlineText: fallback.formattedDeadline
      ? `Deadline: ${fallback.formattedDeadline}`
      : "",
    daysRemaining: null,
    selectedIntake: fallback,
  };
};
